import { Waypoint } from '@omniafishing/core';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { isPendingOrNotStarted, LoadingState } from '../../constants/loading_state';
import { useUser } from '../../hooks/use_user';
import {
  XWeatherConditions,
  xWeatherConditionsAtTimestamp,
  xWeatherConditionsFromTo,
  xWeatherSunMoon,
  XWeatherSunMoon,
} from '../../lib/xweather';
import { CurrentConditions } from './current_conditions';
import { mockSolunarData, mockWeatherConditions, PressureTrend } from './current_conditions_lib';

interface CurrentConditionsTimestampProps {
  timestamp: string;
  lat: number;
  lng: number;
  isProBlocked?: boolean;
  className?: string;
}
// this component creates a placeholder waypoint with api data for the given timestamp
export const CurrentConditionsTimestamp = (props: CurrentConditionsTimestampProps) => {
  const { lat, lng, timestamp, isProBlocked, className } = props;

  const { isPro } = useUser();
  const isNotProAndIsProBlocked = !isPro && isProBlocked;

  const [weatherLoadingState, setWeatherLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [weatherData, setWeatherData] = useState<XWeatherConditions>(mockWeatherConditions);
  const [solunarLoadingState, setSolunarLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [solunarData, setSolunarData] = useState<XWeatherSunMoon>(mockSolunarData);
  const [pressureTrend, setPressureTrend] = useState<PressureTrend>();
  const requestCount = React.useRef(0);

  const fetchAndSetWeatherDebounced = useCallback(
    _.debounce(
      (args: { requestId: number; lat: number; lng: number; timestamp: string }) => {
        const { requestId } = args;
        if (isNotProAndIsProBlocked) {
          return;
        }

        setWeatherLoadingState(LoadingState.PENDING);
        xWeatherConditionsAtTimestamp({
          lat,
          lng,
          timestamp,
        })
          .then((res) => {
            if (requestId !== requestCount.current) {
              return;
            }
            setWeatherData(res.data.response[0].periods[0]);
            setWeatherLoadingState(LoadingState.DONE);
          })
          .catch((e) => {
            console.error('Error fetching weather conditions:', e.message);
          });

        setSolunarLoadingState(LoadingState.PENDING);
        xWeatherSunMoon({
          lat,
          lng,
          timestamp,
        })
          .then((res) => {
            if (requestId !== requestCount.current) {
              return;
            }
            setSolunarData(res.data.response[0]);
            setSolunarLoadingState(LoadingState.DONE);
          })
          .catch((e) => {
            console.error('Error fetching solunar data:', e);
          });

        const previousHour = dayjs(timestamp).subtract(61, 'minutes').toISOString();
        xWeatherConditionsFromTo({
          lat,
          lng,
          from: previousHour,
          to: timestamp,
        })
          .then((res) => {
            if (requestId !== requestCount.current) {
              return;
            }
            const [prev, current] = res.data.response[0].periods;
            if (!prev || !current) {
              return;
            }
            let newPressureTrend: PressureTrend = 'steady';
            if (current.spressureIN < prev.spressureIN) {
              newPressureTrend = 'falling';
            } else if (current.spressureIN > prev.spressureIN) {
              newPressureTrend = 'rising';
            }
            setPressureTrend(newPressureTrend);
          })
          .catch((e) => {
            console.error('Error fetching weather conditions:', e.message);
          });
      },
      1000,
      {
        trailing: true,
        leading: true,
      }
    ),
    []
  );

  useEffect(() => {
    requestCount.current += 1;
    fetchAndSetWeatherDebounced({
      requestId: requestCount.current,
      lat,
      lng,
      timestamp,
    });
  }, [lat, lng, timestamp]);

  const waypoint: Waypoint = {
    air_temperature_f: weatherData.tempF,
    barometer_in_hg: weatherData.spressureIN,
    barometer_trend: pressureTrend,
    cloud_cover_percent: weatherData.sky,
    comments: '',
    conditions_saved: false,
    created_at: '',
    icon: weatherData.icon,
    id_hash: '',
    id: -1,
    img: '',
    image_dimensions: {
      height: 0,
      width: 0,
      portrait: false,
      landscape: false,
    },
    lat,
    lng,
    moon_overhead_at: solunarData.moon.transitISO,
    moon_percent: solunarData.moon.phase.illum,
    moon_phase: solunarData.moon.phase.name,
    moon_underfoot_at: solunarData.moon.underfootISO,
    moonrise: solunarData.moon.riseISO,
    moonset: solunarData.moon.setISO,
    name: '',
    outing_at: timestamp,
    precipitation_percent: weatherData.pop,
    public: false,
    sunrise: solunarData.sun.riseISO,
    sunset: solunarData.sun.setISO,
    symbol_imported: null,
    timezone: solunarData.profile.tz,
    updated_at: '',
    water_clarity: null,
    water_depth_ft: null,
    water_temperature_f: null,
    waterbody: null,
    waypoint_type: 'marker',
    weather_name: weatherData.weatherPrimary,
    wind_direction: weatherData.windDirDEG,
    wind_mph: weatherData.windSpeedMPH,
  };

  return (
    <CurrentConditions
      waypoint={waypoint}
      isProBlocked={isProBlocked}
      solunarLoading={isPendingOrNotStarted(solunarLoadingState)}
      weatherLoading={isPendingOrNotStarted(weatherLoadingState)}
      className={className}
    />
  );
};
